import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ManagerApi from '../../api/ManagerApi';
import { FullCourtDto, TraitDefinition } from '../../interfaces/CourtInterfaces';
import TraitPicker from '../TraitPicker/TraitPicker';
import CourtDifficultyChip from './CourtDifficultyChip';

/**
 * Lloyd's court ratings (6 Oct 2026). Its own card with its own Save, so ticking a trait never
 * means opening the whole court record for editing.
 *
 * The score is the server's, recomputed on save, so this card and the calendar warning cannot
 * disagree about the same court.
 */
interface CourtRatingsCardProps {
  court: FullCourtDto;
  definitions: TraitDefinition[];
  onSaved: (court: FullCourtDto) => void;
}

const CourtRatingsCard: React.FC<CourtRatingsCardProps> = ({ court, definitions, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [traits, setTraits] = useState<string[]>([]);
  const [otherNegative, setOtherNegative] = useState('');
  const [otherPositive, setOtherPositive] = useState('');

  const reset = () => {
    setTraits(court.Court_Traits ?? []);
    setOtherNegative(court.Court_TraitsOtherNegative ?? '');
    setOtherPositive(court.Court_TraitsOtherPositive ?? '');
  };

  useEffect(reset, [court]);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      const updated = await ManagerApi.updateCourt(court.Court_guid, {
        Court_Traits: traits,
        Court_TraitsOtherNegative: otherNegative,
        Court_TraitsOtherPositive: otherPositive,
      });
      // PATCH answers with the court but not its prosecutors; keep the ones already on screen.
      onSaved({ ...court, ...updated, prosecutors: court.prosecutors });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the ratings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2} gap={1}>
          <Avatar sx={{ mr: 1, bgcolor: 'warning.main' }}>
            <StarHalfIcon />
          </Avatar>
          <Typography variant="h6" component="h2">
            Court ratings
          </Typography>
          {court.Court_Difficulty && (
            <Box ml={1}>
              <CourtDifficultyChip difficulty={court.Court_Difficulty} showWhenNone />
            </Box>
          )}
          <Box ml="auto" display="flex" gap={1}>
            {editing ? (
              <>
                <IconButton onClick={save} size="small" color="primary" disabled={saving} title="Save ratings">
                  <SaveIcon />
                </IconButton>
                <IconButton
                  onClick={() => {
                    reset();
                    setEditing(false);
                    setError(null);
                  }}
                  size="small"
                  color="secondary"
                  disabled={saving}
                  title="Cancel"
                >
                  <CancelIcon />
                </IconButton>
              </>
            ) : (
              <IconButton onClick={() => setEditing(true)} size="small" title="Edit ratings">
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TraitPicker
          definitions={definitions}
          selected={traits}
          onChange={setTraits}
          detected={court.Court_DetectedTraits}
          otherNegative={otherNegative}
          otherPositive={otherPositive}
          onOtherChange={(side, value) =>
            side === 'negative' ? setOtherNegative(value) : setOtherPositive(value)
          }
          readOnly={!editing}
          disabled={saving}
        />

        {editing && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Hearings at a court scored “Watch” or “Difficult” show a warning on the calendar.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CourtRatingsCard;
